import { apiFetch, buildQuery } from "./client";
import type { Page } from "./admin";

/** Sprint 1 contract: docs/api/sprint1-lokacije-katalog.md §1; kinds moved to a
 * DB catalogue in sprint 5a (docs/api/sprint5-vrste-lokacija.md). */

/** Code of a `location_kind` row — admin-defined, no longer a fixed union. */
export type LocationKind = string;

export type LocationKindIcon = "pin" | "factory" | "building" | "door" | "warehouse" | "list"
  | "grid" | "layers" | "tools" | "box" | "truck" | "world"
  | "home" | "office" | "garage" | "hangar" | "gate" | "fence" | "silo" | "tank"
  | "barrel" | "pallet" | "container" | "shelves" | "stairs" | "elevator" | "parking" | "car"
  | "forklift" | "crane" | "flask" | "snowflake" | "leaf" | "pine" | "pipe" | "fan"
  | "fire" | "drop" | "bolt" | "cog" | "line" | "tree" | "tag" | "user" | "materials" | "commercial" | "book" | "alert";

/** What the app does with a location of this kind (fixed set, chosen by the admin). */
export type LocationKindBehavior = "NONE" | "EXTERNAL_SERVICE" | "WORKSHOP";

export const LOCATION_KIND_BEHAVIORS: readonly LocationKindBehavior[] = ["NONE", "EXTERNAL_SERVICE", "WORKSHOP"];

/** Code regex enforced server-side (`^[A-Z][A-Z0-9_]{1,29}$`). */
export const LOCATION_KIND_CODE_PATTERN = /^[A-Z][A-Z0-9_]{1,29}$/;

export type LocationKindView = {
  code: string;
  name: string;
  icon: LocationKindIcon;
  behavior: LocationKindBehavior;
  sortOrder: number;
  /** System kinds cannot be deleted nor have their behavior changed. */
  isSystem: boolean;
  locationCount: number;
};

export type LocationKindRequest = {
  /** Ignored on PUT. */
  code: string;
  name: string;
  behavior?: LocationKindBehavior;
  sortOrder?: number;
  /** Defaults to pin on POST; omitted on PUT keeps the current icon. */
  icon?: LocationKindIcon;
};

export const locationKindsApi = {
  list: () => apiFetch<LocationKindView[]>("/location-kinds"),
  create: (body: LocationKindRequest) =>
    apiFetch<LocationKindView>("/location-kinds", { method: "POST", body }),
  update: (code: string, body: LocationKindRequest) =>
    apiFetch<LocationKindView>(`/location-kinds/${encodeURIComponent(code)}`, { method: "PUT", body }),
  remove: (code: string) =>
    apiFetch<void>(`/location-kinds/${encodeURIComponent(code)}`, { method: "DELETE" }),
};

export type PathEntry = { id: string; name: string };

export type LocationView = {
  id: string;
  parentId: string | null;
  code: string;
  name: string;
  /** Kind code (FK to `location_kind`). */
  kind: LocationKind;
  kindName: string;
  kindBehavior: LocationKindBehavior;
  lat: number | null;
  lng: number | null;
  effectiveLat: number | null;
  effectiveLng: number | null;
  note: string | null;
  sortOrder: number;
  /** Root -> parent (without the node itself). */
  path: PathEntry[];
  createdAt: string;
  updatedAt: string;
};

export type LocationNode = Omit<LocationView, "path" | "effectiveLat" | "effectiveLng"> & {
  children: LocationNode[];
};

export type LocationRequest = {
  parentId?: string | null;
  code: string;
  name: string;
  kind: LocationKind;
  lat?: number | null;
  lng?: number | null;
  note?: string | null;
  sortOrder?: number;
};

export type LocationListParams = {
  q?: string;
  kind?: LocationKind;
  parentId?: string;
  page?: number;
  size?: number;
  sort?: string;
};

export const locationsApi = {
  tree: () => apiFetch<LocationNode[]>("/locations/tree"),
  list: (params: LocationListParams = {}) =>
    apiFetch<Page<LocationView>>(`/locations${buildQuery(params)}`),
  get: (id: string) => apiFetch<LocationView>(`/locations/${id}`),
  create: (body: LocationRequest) =>
    apiFetch<LocationView>("/locations", { method: "POST", body }),
  update: (id: string, body: LocationRequest) =>
    apiFetch<LocationView>(`/locations/${id}`, { method: "PUT", body }),
  /** `reason` is mandatory (2026-09-22). */
  remove: (id: string, reason: string) => apiFetch<void>(`/locations/${id}?reason=${encodeURIComponent(reason)}`, { method: "DELETE" }),
};
